import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCollection, createCollection, updateCollection, updateCollectionStatus, fetchTemplates } from '../api/client';
import FlowCanvas from '../components/flow/FlowCanvas';
import NodeEditor from '../components/flow/NodeEditor';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

function generateId() {
  return `step_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

const DEFAULT_COLLECTION = {
  collectionId: '',
  name: '',
  description: '',
  triggerEvent: '',
  cancellationEvents: [],
  steps: [
    { id: 'trigger', type: 'trigger', event: '' },
    { id: 'exit', type: 'exit' },
  ],
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', style: 'bg-success/15 text-success border-success/30' },
  { value: 'inactive', label: 'Inactive', style: 'bg-surface text-text-secondary border-ink/20' },
  { value: 'archived', label: 'Archived', style: 'bg-alert/10 text-alert border-alert/30' },
];

export default function WingEditor() {
  const { collectionId, stepId } = useParams();
  const isNew = !collectionId;
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  const [collection, setCollection] = useState(null);
  const [original, setOriginal] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(stepId || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Test event state
  const [testDistinctId, setTestDistinctId] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testFirstName, setTestFirstName] = useState('');
  const [firingEvent, setFiringEvent] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Load collection + templates
  const loadData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();

      const tplResult = await fetchTemplates(token);
      setTemplates(tplResult.templates || tplResult || []);

      if (isNew) {
        setCollection({ ...DEFAULT_COLLECTION, steps: [...DEFAULT_COLLECTION.steps.map(s => ({ ...s }))] });
        setOriginal(null);
      } else {
        const result = await fetchCollection(token, collectionId);
        setCollection(result.collection);
        setOriginal(JSON.parse(JSON.stringify(result.collection)));
        if (stepId) setSelectedNodeId(stepId);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, collectionId, isNew, stepId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pre-fill test fields from auth user
  useEffect(() => {
    if (user?.email) {
      setTestEmail(user.email);
      setTestDistinctId(`test-${user.email.split('@')[0]}-${Date.now().toString(36)}`);
      setTestFirstName(user.given_name || user.nickname || 'Test');
    }
  }, [user]);

  const isDirty = collection && JSON.stringify(collection) !== JSON.stringify(original);

  const handleSave = async () => {
    if (!collection) return;
    setSaving(true);
    setMessage(null);

    try {
      const token = await getAccessTokenSilently();

      if (isNew) {
        if (!collection.collectionId || !collection.name || !collection.triggerEvent) {
          setMessage({ type: 'error', text: 'Collection ID, name, and trigger event are required' });
          setSaving(false);
          return;
        }
        const steps = collection.steps.map(s =>
          s.type === 'trigger' ? { ...s, event: collection.triggerEvent } : s
        );
        const emailCount = steps.filter(s => s.type === 'email').length;
        const result = await createCollection(token, { ...collection, steps, emailCount, status: 'inactive' });
        setMessage({ type: 'success', text: 'Collection created' });
        navigate(`/wings/${result.collection.collectionId}`, { replace: true });
      } else {
        const emailCount = collection.steps.filter(s => s.type === 'email').length;
        const result = await updateCollection(token, collectionId, {
          ...collection,
          emailCount,
          version: collection.version,
        });
        setCollection(result.collection);
        setOriginal(JSON.parse(JSON.stringify(result.collection)));
        setMessage({ type: 'success', text: 'Changes saved' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isNew) {
      navigate('/wings');
    } else {
      setCollection(JSON.parse(JSON.stringify(original)));
      setSelectedNodeId(null);
      setMessage(null);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!collectionId || isNew) return;
    setTogglingStatus(true);
    setMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await updateCollectionStatus(token, collectionId, newStatus);
      setCollection(prev => ({ ...prev, status: newStatus }));
      setOriginal(prev => prev ? { ...prev, status: newStatus } : prev);
      setMessage({ type: 'success', text: `Status changed to ${newStatus}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleFireTestEvent = async () => {
    if (!collection?.triggerEvent) return;
    setFiringEvent(true);
    setTestResult(null);

    const distinctId = testDistinctId || `test-${Date.now()}`;
    const eventName = collection.triggerEvent;

    try {
      if (!POSTHOG_KEY) {
        setTestResult({ type: 'error', text: 'VITE_POSTHOG_KEY not configured in .env' });
        setFiringEvent(false);
        return;
      }

      const res = await fetch(`${POSTHOG_HOST}/capture/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: POSTHOG_KEY,
          event: eventName,
          distinct_id: distinctId,
          properties: {
            $user_id: distinctId,
            email: testEmail || undefined,
            firstName: testFirstName || undefined,
            $set: {
              email: testEmail || undefined,
              firstName: testFirstName || undefined,
            },
          },
        }),
      });

      if (res.ok) {
        setTestResult({
          type: 'success',
          text: `Fired "${eventName}" for ${distinctId}`,
        });
      } else {
        const body = await res.text();
        setTestResult({ type: 'error', text: `PostHog ${res.status}: ${body}` });
      }
    } catch (err) {
      setTestResult({ type: 'error', text: err.message });
    } finally {
      setFiringEvent(false);
      setTimeout(() => setTestResult(null), 8000);
    }
  };

  // Step operations
  const handleAddStep = (type, insertIndex) => {
    const newStep = { id: generateId(), type };

    switch (type) {
      case 'email':
        newStep.templateId = '';
        newStep.stepNumber = (collection.steps.filter(s => s.type === 'email').length || 0) + 1;
        newStep.name = '';
        break;
      case 'wait':
        newStep.duration = 86400;
        newStep.label = '1 day';
        break;
      case 'condition':
        newStep.field = '';
        newStep.operator = 'eq';
        newStep.value = '';
        newStep.label = '';
        break;
      case 'exit':
        newStep.reason = '';
        break;
    }

    const steps = [...collection.steps];
    steps.splice(insertIndex, 0, newStep);
    setCollection({ ...collection, steps });
    setSelectedNodeId(newStep.id);
  };

  const handleNodeUpdate = (updatedStep) => {
    const steps = collection.steps.map(s => s.id === updatedStep.id ? updatedStep : s);
    setCollection({ ...collection, steps });
  };

  const handleNodeDelete = (stepId) => {
    const steps = collection.steps.filter(s => s.id !== stepId);
    setCollection({ ...collection, steps });
    setSelectedNodeId(null);
  };

  const selectedStep = collection?.steps?.find(s => s.id === selectedNodeId);
  const emailCount = collection?.steps?.filter(s => s.type === 'email').length || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-alert/10 border-2 border-alert rounded-xl p-6 shadow-brutal">
        <p className="text-alert font-semibold">Failed to load collection</p>
        <p className="text-text-secondary text-sm mt-1">{error}</p>
        <button onClick={() => navigate('/wings')} className="mt-3 text-sm text-primary font-medium hover:underline">
          Back to Wings
        </button>
      </div>
    );
  }

  if (!collection) return null;

  const currentStatus = STATUS_OPTIONS.find(s => s.value === collection.status) || STATUS_OPTIONS[1];

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/wings')}
            className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary border-2 border-ink/30 rounded-xl hover:bg-surface transition-all duration-200 hover:-translate-y-0.5"
          >
            Back
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-text-primary truncate">
                {collection.name || 'Untitled Collection'}
              </h2>
              {!isNew && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${currentStatus.style}`}>
                  {currentStatus.label}
                </span>
              )}
            </div>
            <p className="text-sm text-text-secondary truncate">
              {collection.description || (isNew ? 'New collection' : collection.collectionId)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-text-secondary font-medium">
            {emailCount} {emailCount === 1 ? 'wing' : 'wings'}
          </span>
          {!isNew && (
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`px-3 py-1.5 text-sm font-semibold border-2 rounded-xl transition-all duration-200 hover:-translate-y-0.5 ${
                showSettings
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'text-text-secondary border-ink/30 hover:bg-surface'
              }`}
            >
              <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>
          )}
          {isDirty && (
            <button
              onClick={handleDiscard}
              className="px-4 py-1.5 text-sm font-semibold text-text-secondary border-2 border-ink/30 rounded-xl hover:bg-surface transition-all duration-200 hover:-translate-y-0.5"
            >
              Discard
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || (!isNew && !isDirty)}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-primary rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 active:shadow-brutal-sm transition-all duration-200 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 ${
            message.type === 'success'
              ? 'bg-success/10 text-success border-success'
              : 'bg-alert/10 text-alert border-alert'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Settings Panel (existing collections) */}
      {!isNew && showSettings && (
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-text-primary">Collection Settings</h3>
            <button onClick={() => setShowSettings(false)} className="text-text-secondary hover:text-text-primary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Name</label>
              <input
                type="text"
                value={collection.name}
                onChange={(e) => setCollection({ ...collection, name: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Description</label>
              <input
                type="text"
                value={collection.description || ''}
                onChange={(e) => setCollection({ ...collection, description: e.target.value })}
                placeholder="What this collection does"
                className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Trigger Event</label>
              <input
                type="text"
                value={collection.triggerEvent || ''}
                onChange={(e) => setCollection({ ...collection, triggerEvent: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Cancellation Events</label>
              <input
                type="text"
                value={(collection.cancellationEvents || []).join(', ')}
                onChange={(e) => setCollection({ ...collection, cancellationEvents: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="comma-separated"
                className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary font-mono"
              />
            </div>
          </div>

          {/* Status toggle */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Status</label>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusChange(opt.value)}
                  disabled={togglingStatus || collection.status === opt.value}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-xl border-2 transition-all duration-200 disabled:opacity-50 ${
                    collection.status === opt.value
                      ? `${opt.style} border-current`
                      : 'border-ink/20 text-text-secondary hover:bg-surface hover:-translate-y-0.5'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Collection ID (read-only) */}
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span className="font-semibold">ID:</span>
            <span className="font-mono bg-background border border-ink/10 px-2 py-0.5 rounded">{collection.collectionId}</span>
            {collection.version && (
              <>
                <span className="font-semibold ml-2">Version:</span>
                <span className="font-mono bg-background border border-ink/10 px-2 py-0.5 rounded">{collection.version}</span>
              </>
            )}
          </div>

          {/* Test Event */}
          <div className="border-t-2 border-ink/10 pt-4">
            <h4 className="text-xs font-bold text-text-secondary mb-3 uppercase tracking-wider">Test Event</h4>
            <p className="text-xs text-text-secondary mb-3">
              Fire <span className="font-mono font-semibold text-text-primary">{collection.triggerEvent || '(no trigger)'}</span> to PostHog.
              {collection.status !== 'active' && (
                <span className="text-alert ml-1">Collection is {collection.status} — event will be received but won't enroll.</span>
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">Distinct ID</label>
                <input
                  type="text"
                  value={testDistinctId}
                  onChange={(e) => setTestDistinctId(e.target.value)}
                  placeholder="test-user-123"
                  className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">Email</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">First Name</label>
                <input
                  type="text"
                  value={testFirstName}
                  onChange={(e) => setTestFirstName(e.target.value)}
                  placeholder="Test"
                  className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleFireTestEvent}
                disabled={firingEvent || !collection.triggerEvent}
                className="px-4 py-1.5 text-sm font-semibold text-white bg-yellow-sunny text-ink rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 active:shadow-brutal-sm transition-all duration-200 disabled:opacity-50"
              >
                {firingEvent ? 'Firing...' : 'Fire Test Event'}
              </button>
              {testResult && (
                <span className={`text-sm font-semibold ${testResult.type === 'success' ? 'text-success' : 'text-alert'}`}>
                  {testResult.text}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collection metadata (for new collections) */}
      {isNew && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-surface rounded-xl border-2 border-ink shadow-brutal p-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Collection ID</label>
            <input
              type="text"
              value={collection.collectionId || ''}
              onChange={(e) => setCollection({ ...collection, collectionId: e.target.value.replace(/[^a-z0-9-]/g, '') })}
              placeholder="e.g., collection11-feature"
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Trigger Event</label>
            <input
              type="text"
              value={collection.triggerEvent || ''}
              onChange={(e) => setCollection({ ...collection, triggerEvent: e.target.value })}
              placeholder="e.g., feature_adopted"
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Cancellation Events</label>
            <input
              type="text"
              value={(collection.cancellationEvents || []).join(', ')}
              onChange={(e) => setCollection({ ...collection, cancellationEvents: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              placeholder="e.g., subscription_created"
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
        </div>
      )}

      {/* Collection metadata summary (for existing, when settings closed) */}
      {!isNew && !showSettings && (
        <div className="flex items-center gap-4 text-xs text-text-secondary px-1">
          <span className="font-mono bg-surface border border-ink/20 px-2 py-0.5 rounded">{collection.triggerEvent}</span>
          {collection.cancellationEvents?.length > 0 && (
            <span>
              Cancels on: {collection.cancellationEvents.map(e => (
                <span key={e} className="font-mono bg-surface border border-ink/20 px-1.5 py-0.5 rounded ml-1">{e}</span>
              ))}
            </span>
          )}
        </div>
      )}

      {/* Flow canvas */}
      <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal min-h-[500px] overflow-auto relative" style={{ backgroundImage: 'radial-gradient(circle, #181830 0.5px, transparent 0.5px)', backgroundSize: '20px 20px', backgroundPosition: '10px 10px' }}>
        <FlowCanvas
          steps={collection.steps}
          selectedNodeId={selectedNodeId}
          onNodeClick={(id) => setSelectedNodeId(selectedNodeId === id ? null : id)}
          onAddStep={handleAddStep}
        />
      </div>

      {/* Node editor side panel */}
      {selectedStep && (
        <NodeEditor
          step={selectedStep}
          templates={templates}
          onUpdate={handleNodeUpdate}
          onDelete={handleNodeDelete}
          onClose={() => setSelectedNodeId(null)}
        />
      )}
    </div>
  );
}
